/**
 * Helper functions for feedback functionality
 */

import { RATINGS } from './constants';

/**
 * Calculates the average rating from a set of provided ratings
 * 
 * @param ratings Object containing rating values
 * @returns Average rating value or null if no ratings provided
 */
export const calculateAverageRating = (ratings: Record<string, number | null>): number | null => {
  const validRatings = Object.values(ratings).filter(r => r !== null) as number[];
  
  if (validRatings.length === 0) {
    return null;
  }
  
  return validRatings.reduce((sum, rating) => sum + rating, 0) / validRatings.length;
};

/**
 * Converts a numerical rating to a RATINGS constant
 * 
 * @param rating Numerical rating (1-5)
 * @returns Corresponding RATINGS constant
 */
export const mapRatingToConstant = (rating: number): number => {
  if (rating >= 4.5) {
    return RATINGS.VERY_HAPPY;
  } else if (rating >= 3.5) {
    return RATINGS.PLEASED;
  } else {
    return RATINGS.UNHAPPY;
  }
};

/**
 * Validates if the feedback form has sufficient data to submit
 * 
 * @param ratings Object containing rating values
 * @param futureCollab Future collaboration selection
 * @returns Boolean indicating if the form is valid for submission
 */
export const isFeedbackFormValid = (
  ratings: Record<string, number | null>,
  futureCollab: string | null
): boolean => {
  const hasAnyRating = Object.values(ratings).some(rating => rating !== null);
  const hasFutureCollab = futureCollab !== null;
  
  return hasAnyRating || hasFutureCollab;
};

/**
 * Formats feedback data for API submission
 * 
 * @param ratings Object containing rating values
 * @param feedbackComments Object containing feedback comments
 * @returns Formatted feedback details object
 */
export const formatFeedbackDetails = (
  ratings: Record<string, number | null>,
  feedbackComments: Record<string, string>
): Record<string, { rating: number | null; comment: string }> => {
  return Object.keys(ratings).reduce((result, category) => {
    return {
      ...result,
      [category]: {
        rating: ratings[category],
        comment: feedbackComments[category] || ''
      }
    };
  }, {});
}; 